// Assets tab of the creator asset library page: keeps the table in sync while
// uploads scan. Rows marked data-pending poll the statuses endpoint until the
// scan promotes (published) or rejects the staged file; each result carries
// fresh row_html so the server stays the single source of row markup.
import Tagify from "@yaireo/tagify"
import { displayFlashMessage, csrfToken } from "../../../util/methods"
import { presignAndUpload } from "../../../components/s3_upload/presign_upload"
import {
  createStatusPoller,
  stillScanningNotice,
} from "../../../components/live_status"
import { enableRangeSelect } from "../../../components/range_select"

function page() {
  return document.getElementById("js-asset-library-page")
}

// Publishing is a creator concept — customers' personal libraries deliver
// assets to Blender regardless of status, so their flashes must not tell
// them to publish (there's no publish control to act on).
function publishingEnabled() {
  var root = page()
  return !root || root.getAttribute("data-publishing") !== "false"
}

// Both pollers below (asset rows and container-upload rows) batch the pending
// rows' ids into the statuses endpoint; the shared engine reschedules while
// pending rows remain.
function pendingRows(rowSelector) {
  return Array.prototype.slice.call(
    document.querySelectorAll(rowSelector + "[data-pending='true']")
  )
}

function rowPollerOptions(config) {
  return {
    page: page,
    maxPolls: config.maxPolls,
    onResults: config.onResults,
    onBudgetExhausted: stillScanningNotice,
    hasPending: function () {
      return pendingRows(config.rowSelector).length > 0
    },
    url: function () {
      var query = new URLSearchParams()
      pendingRows(config.rowSelector).forEach(function (row) {
        query.append("ids[]", row.getAttribute(config.idAttribute))
      })
      return page().getAttribute(config.urlAttribute) + "?" + query.toString()
    },
  }
}

// --- Asset-row polling ---

// Replaces the row for a statuses/create result, or appends a new one.
export function upsertAssetRow(result) {
  var existing = document.querySelector(
    ".js-asset-row[data-asset-id='" + result.id + "']"
  )
  if (existing) {
    $(existing).replaceWith(result.row_html)
    return
  }

  var tbody = document.getElementById("js-assets-tbody")
  if (!tbody) return
  $(tbody).find(".js-assets-empty").remove()
  // Newest first, matching the default table order.
  $(tbody).prepend(result.row_html)
}

// Scan results arrive while the user may be looking elsewhere, so their
// toasts persist until dismissed instead of auto-hiding.
function applyAssetStatusResult(result) {
  if (result.pending_file) return

  upsertAssetRow(result)
  if (result.status === "rejected") {
    var reasons = (result.rejection_reasons || [])
      .map(function (reason) {
        return reason.message
      })
      .join(" ")
    displayFlashMessage("danger", "An upload was rejected. " + reasons, {
      persist: true,
    })
  } else if (result.status === "published") {
    displayFlashMessage("success", "Scan finished — asset updated.", {
      persist: true,
    })
  } else if (result.status === "pending") {
    displayFlashMessage(
      "success",
      publishingEnabled()
        ? "Scan finished — asset ready to publish."
        : "Scan finished — asset ready.",
      { persist: true }
    )
  }
}

var assetPoller = createStatusPoller(
  rowPollerOptions({
    rowSelector: ".js-asset-row",
    idAttribute: "data-asset-id",
    urlAttribute: "data-statuses-url",
    maxPolls: 40,
    onResults: function (results) {
      results.forEach(applyAssetStatusResult)
      return false
    },
  })
)

export function startStatusPolling() {
  assetPoller.start()
}

// --- Container-upload (file) polling ---
// A dropped .blend shows one Scanning placeholder row; when the shim finishes,
// the placeholder explodes into a row per detected asset (or re-renders with
// the rejection reasons). A dropped .zip shows one Unpacking row that explodes
// into a Scanning row per .blend inside.

export function appendFileScanRow(result) {
  var tbody = document.getElementById("js-assets-tbody")
  if (!tbody) return
  $(tbody).find(".js-assets-empty").remove()
  $(tbody).prepend(result.placeholder_html)
}

// Zip batches scan for a while: the budget is generous and resets whenever a
// poll cycle makes progress, so it only ends polling when nothing is moving.
var filePoller = createStatusPoller(
  rowPollerOptions({
    rowSelector: ".js-file-scan-row",
    idAttribute: "data-file-id",
    urlAttribute: "data-file-statuses-url",
    maxPolls: 100,
    onResults: function (results) {
      // One aggregated set of notices per poll cycle — a zip batch resolves
      // dozens of rows over a few cycles and must not fire a toast per file.
      var summary = newCycleSummary()
      results.forEach(function (result) {
        applyFileScanResult(result, summary)
      })
      flushCycleSummary(summary)
      if (results.length) {
        updateFilingIndicators(results[results.length - 1].needs_remap_count)
      }
      return summary.progressed
    },
  })
)

export function startFilePolling() {
  filePoller.start()
}

// The Catalogs tab renders at page load and doesn't live-update; scans that
// finish mid-visit change the needs-remap count, so the poll response carries
// it and we keep the tab badge honest plus surface a refresh hint inside the
// tab once it goes stale.
function updateFilingIndicators(count) {
  if (typeof count !== "number") return

  var tab = document.getElementById("catalogs-tab")
  if (tab) {
    var badge = tab.querySelector(".badge")
    if (count > 0) {
      if (!badge) {
        badge = document.createElement("span")
        badge.className = "badge bg-warning text-dark"
        tab.appendChild(badge)
      }
      badge.textContent = count
    } else if (badge) {
      badge.remove()
    }
  }

  var hint = document.querySelector(".js-filing-stale")
  if (hint) {
    var initial = parseInt(hint.getAttribute("data-initial-remap-count"), 10)
    hint.classList.toggle("d-none", count === initial)
  }
}

function newCycleSummary() {
  return {
    progressed: false,
    scanned: 0,
    assetsReady: 0,
    deduplicated: 0,
    unpacked: [],
    rejected: [],
    awaitingConfirmation: 0,
    awaitingSupport: 0,
  }
}

function pluralizeFiles(count) {
  return count + (count === 1 ? " file" : " files")
}

// Scan-cycle summaries land asynchronously, so they persist until dismissed.
function flushCycleSummary(summary) {
  summary.unpacked.forEach(function (zip) {
    var catalogs = zip.catalogs
    var message = "Unpacked " + pluralizeFiles(zip.count) + " from " + zip.name
    var type = "info"
    if (catalogs && catalogs.error) {
      message += " — catalog import failed: " + catalogs.error
      type = "warning"
    } else if (catalogs) {
      var imported = (catalogs.created || 0) + (catalogs.updated || 0)
      if (imported > 0) message += " — imported " + imported + " catalogs"
    }
    if (zip.skipped && zip.skipped.length) {
      message +=
        " — skipped " + pluralizeFiles(zip.skipped.length) + " over 4 GB"
      type = "warning"
    }
    displayFlashMessage(type, message + " — scanning…", { persist: true })
  })

  if (summary.scanned > 0) {
    displayFlashMessage(
      "success",
      pluralizeFiles(summary.scanned) +
        " scanned — " +
        summary.assetsReady +
        (summary.assetsReady === 1 ? " asset" : " assets") +
        " ready." +
        (publishingEnabled() ? " Publish them when you're ready." : ""),
      { persist: true }
    )
  }

  if (summary.deduplicated > 0) {
    displayFlashMessage(
      "info",
      pluralizeFiles(summary.deduplicated) +
        " matched uploads that were already processed — nothing was changed or replaced.",
      { persist: true }
    )
  }

  if (summary.awaitingConfirmation > 0) {
    displayFlashMessage(
      "warning",
      pluralizeFiles(summary.awaitingConfirmation) +
        " contain assets that already exist in this library — review the rows above" +
        (publishingEnabled() ? " before publishing." : "."),
      { persist: true }
    )
  }

  if (summary.awaitingSupport > 0) {
    displayFlashMessage(
      "info",
      pluralizeFiles(summary.awaitingSupport) +
        " were saved with a newer Blender than we support yet — they will be processed automatically once support lands.",
      { persist: true }
    )
  }

  if (summary.rejected.length > 0) {
    var reasons = summary.rejected.slice(0, 3).join(" ")
    if (summary.rejected.length > 3) {
      reasons +=
        " (and " +
        (summary.rejected.length - 3) +
        " more — see the rows above.)"
    }
    displayFlashMessage(
      "danger",
      pluralizeFiles(summary.rejected.length) + " rejected. " + reasons,
      { persist: true }
    )
  }
}

// With a summary (polling path) notices aggregate per cycle; without one
// (confirm/cancel button responses) they flash immediately, as before.
function applyFileScanResult(result, summary) {
  var row = document.querySelector(
    ".js-file-scan-row[data-file-id='" + result.id + "']"
  )
  if (!row) return

  // Still pending: patch the live progress note in place (a row swap would
  // eat the Cancel button's focus). A changed note counts as progress so the
  // poll budget keeps resetting while a big zip stages — only a genuinely
  // stalled upload runs the budget out.
  if (result.status === "pending") {
    var note = row.querySelector(".js-scan-note")
    if (
      note &&
      result.scan_note &&
      note.textContent.trim() !== result.scan_note
    ) {
      note.textContent = result.scan_note
      if (summary) summary.progressed = true
    }
    return
  }

  var cycle = summary || newCycleSummary()
  cycle.progressed = true

  if (result.status === "rejected") {
    $(row).replaceWith(result.row_html)
    var reasons = (result.rejection_reasons || [])
      .map(function (reason) {
        return reason.message
      })
      .join(" ")
    cycle.rejected.push(reasons)
  } else if (result.status === "awaiting_confirmation") {
    $(row).replaceWith(result.row_html)
    cycle.awaitingConfirmation += 1
  } else if (result.status === "awaiting_support") {
    $(row).replaceWith(result.row_html)
    cycle.awaitingSupport += 1
  } else if (result.spawned_rows && result.spawned_rows.length) {
    // processed zip: the Unpacking row becomes one Scanning row per .blend
    // inside; those land pending, so polling carries straight on.
    $(row).remove()
    var tbody = document.getElementById("js-assets-tbody")
    result.spawned_rows.forEach(function (spawned) {
      var existing = document.querySelector(
        ".js-file-scan-row[data-file-id='" + spawned.id + "']"
      )
      if (!existing && tbody) $(tbody).prepend(spawned.row_html)
    })
    cycle.unpacked.push({
      name: (result.zip && result.zip.filename) || "the zip",
      count: result.spawned_rows.length,
      catalogs: result.zip && result.zip.catalogs,
      skipped: result.zip && result.zip.skipped,
    })
  } else {
    // processed: the placeholder becomes the detected asset rows
    $(row).remove()
    result.asset_rows.forEach(upsertAssetRow)
    if (result.deduplicated) {
      cycle.deduplicated += 1
    } else {
      cycle.scanned += 1
      cycle.assetsReady += result.asset_rows.length
    }
  }

  if (!summary) flushCycleSummary(cycle)
}

// --- Replace confirmation ---
// Rows awaiting confirmation carry Replace/Cancel buttons; the confirm
// response has the same shape as a statuses entry, so it feeds straight back
// through applyFileScanResult.

function setRowBusy(row, busy) {
  row.querySelectorAll("button").forEach(function (button) {
    button.disabled = busy
  })
}

$(document).on("click", ".js-file-confirm", function () {
  var row = this.closest(".js-file-scan-row")
  setRowBusy(row, true)

  fetch(this.getAttribute("data-url"), {
    method: "POST",
    headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
  })
    .then(function (response) {
      if (!response.ok) throw new Error("confirm failed")
      return response.json()
    })
    .then(applyFileScanResult)
    .catch(function () {
      setRowBusy(row, false)
      displayFlashMessage(
        "danger",
        "Could not apply the upload. Please try again."
      )
    })
})

$(document).on("click", ".js-file-cancel", function () {
  var row = this.closest(".js-file-scan-row")
  setRowBusy(row, true)

  fetch(this.getAttribute("data-url"), {
    method: "DELETE",
    headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
  })
    .then(function (response) {
      if (!response.ok) throw new Error("cancel failed")
      $(row).remove()
      displayFlashMessage("info", "Upload discarded — nothing was replaced.")
    })
    .catch(function () {
      setRowBusy(row, false)
      displayFlashMessage(
        "danger",
        "Could not discard the upload. Please try again."
      )
    })
})

// --- Bulk selection ---

function selectedGuids() {
  return $(".js-asset-check:checked")
    .map(function () {
      return this.value
    })
    .get()
}

function updateBulkToolbar() {
  var count = selectedGuids().length
  $(".js-bulk-toolbar")
    .toggleClass("d-none", count === 0)
    .toggleClass("d-flex", count > 0)
  $(".js-bulk-count").text(count + " selected on this page")
}

$(document).on("change", ".js-asset-check", updateBulkToolbar)

enableRangeSelect(".js-asset-check")

$(document).on("change", ".js-asset-check-all", function () {
  $(".js-asset-check").prop("checked", this.checked)
  updateBulkToolbar()
})

$(document).on("click", ".js-bulk-clear", function () {
  $(".js-asset-check, .js-asset-check-all").prop("checked", false)
  updateBulkToolbar()
})

$(document).on(
  "show.bs.modal",
  "#bulkCatalogModal, #bulkMetadataModal, #bulkMoveModal",
  function () {
    var guids = selectedGuids()
    $(this)
      .find(".js-bulk-modal-count")
      .text(guids.length + " assets")
    var container = $(this).find(".js-bulk-ids").empty()
    guids.forEach(function (guid) {
      container.append(
        $("<input>", { type: "hidden", name: "asset_ids[]", value: guid })
      )
    })
  }
)

// Bulk publish/unpublish/delete: plain form POST built from the current
// selection. Destructive buttons set data-confirm-message ({count} is
// replaced with the selection size).
$(document).on("click", ".js-bulk-post", function () {
  var guids = selectedGuids()
  if (!guids.length) return

  var confirmMessage = this.getAttribute("data-confirm-message")
  if (
    confirmMessage &&
    !window.confirm(confirmMessage.replace("{count}", guids.length))
  ) {
    return
  }

  var form = $("<form>", {
    method: "post",
    action: this.getAttribute("data-url"),
  })
  form.append(
    $("<input>", {
      type: "hidden",
      name: "authenticity_token",
      value: csrfToken(),
    })
  )
  guids.forEach(function (guid) {
    form.append(
      $("<input>", { type: "hidden", name: "asset_ids[]", value: guid })
    )
  })
  $(document.body).append(form)
  form[0].submit()
})

// --- Edit modal ---

var editTagify = null

$(document).on("show.bs.modal", "#assetEditModal", function (event) {
  var row = event.relatedTarget && event.relatedTarget.closest(".js-asset-row")
  if (!row) return

  var data = JSON.parse(row.getAttribute("data-asset"))
  var form = this.querySelector(".js-asset-edit-form")
  form.reset()
  form.dataset.url = data.update_url

  var fields = [
    "name",
    "id_type",
    "catalog_path",
    "description",
    "author",
    "license",
    "copyright",
    "bl_version_min",
    "bl_version_until",
  ]
  fields.forEach(function (field) {
    form.querySelector("[name='" + field + "']").value = data[field] || ""
  })
  // The catalog picker is a jsTree over a hidden input — it renders its own
  // control, so tell it the underlying value changed (form.reset() above is
  // invisible to it too; central_catalog_tree.js resyncs on "change").
  $(form.querySelector("[name='catalog_path']")).trigger("change")

  if (editTagify) {
    editTagify.removeAllTags()
    if (data.tags && data.tags.length) editTagify.addTags(data.tags)
  }

  // Show what the owner's own Blender file called this asset's catalog —
  // context for picking the right central catalog. The raw uuid only adds
  // information while the catalog is still unresolved centrally.
  var $detectedNote = $(this).find(".js-asset-edit-detected")
  $detectedNote.toggleClass("d-none", !data.own_catalog_path)
  $detectedNote.find("span").text(data.own_catalog_path || "")
  $detectedNote
    .find("code")
    .text(
      !data.catalog_path && data.detected_catalog_uuid
        ? data.detected_catalog_uuid
        : ""
    )
})

$(document).on("submit", ".js-asset-edit-form", function (event) {
  event.preventDefault()
  var form = this

  var payload = {}
  ;[
    "name",
    "id_type",
    "catalog_path",
    "description",
    "author",
    "license",
    "copyright",
    "bl_version_min",
    "bl_version_until",
  ].forEach(function (field) {
    payload[field] = form.querySelector("[name='" + field + "']").value.trim()
  })
  payload.tags = editTagify
    ? editTagify.value.map(function (tag) {
        return tag.value
      })
    : []

  var thumbnailFile = form.querySelector("[name='thumbnail_file']").files[0]

  var saveButton = form.querySelector(".js-asset-edit-save")
  saveButton.disabled = true

  var uploads = Promise.resolve()
  if (thumbnailFile) {
    uploads = uploads
      .then(function () {
        return presignAndUpload(thumbnailFile, "/s3/public_params")
      })
      .then(function (cached) {
        payload.thumbnail = cached
      })
  }

  uploads
    .then(function () {
      return fetch(form.dataset.url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify(payload),
      })
    })
    .then(function (response) {
      return response.json().then(function (json) {
        return { ok: response.ok, json: json }
      })
    })
    .then(function (result) {
      if (result.ok) {
        upsertAssetRow(result.json)
        if (result.json.pending_file) startStatusPolling()
        form.querySelector("[data-bs-dismiss]").click()
        displayFlashMessage("success", "Asset updated.")
      } else {
        var messages =
          (result.json.errors || [])
            .map(function (err) {
              return err.message
            })
            .join(" ") || "Update failed."
        displayFlashMessage("danger", messages)
      }
    })
    .catch(function () {
      displayFlashMessage("danger", "Update failed. Please try again.")
    })
    .then(function () {
      saveButton.disabled = false
    })
})

$(document).on("turbolinks:load", function () {
  if (!page()) return
  if (assetPoller.hasPending()) assetPoller.start()
  if (filePoller.hasPending()) filePoller.start()

  var tagsInput = document.querySelector("#assetEditModal .js-asset-tags")
  if (tagsInput) {
    editTagify = new Tagify(tagsInput, {
      placeholder: "Tags ...",
      delimiters: ",",
      maxTags: 50,
    })
  }
})

$(document).on("turbolinks:before-cache", function () {
  assetPoller.stop()
  filePoller.stop()
  // Turbolinks caches the Tagify-mutated DOM; without a destroy, restoring
  // from cache and re-initializing would wrap the input a second time.
  if (editTagify) {
    editTagify.destroy()
    editTagify = null
  }
})
